import { useEffect, useCallback, useRef } from 'react';
import * as PIXI from 'pixi.js';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import useSound from 'use-sound';
import { useLatest } from '../../hooks/useLatest';
import useSoundStore from '../../stores/useSoundStore';
import { deleteCobweb } from './furnitureApi';
import { destroyCobwebSprite, syncCobwebSprites } from './render/cobweb';
import type { CobwebItem } from './cobwebLayout';
import type { RoomPixiRoots } from './useRoomScene';
import type { RoomSceneState } from './state/roomSceneState';

// ── Cobweb sprite sync + sweep sound cluster ──
// Extracted verbatim from RoomCanvas (decomposition final cleanup pass):
//  * sweep sound setup (useSound + fade helper + freshness refs) — sole consumer
//    is the cobweb tap handler, so the whole cluster moved with it;
//  * lightweight footprint-visibility toggle on grid-mode change;
//  * incremental cobweb sprite sync effect (delegates to render/cobweb.ts).
// Effect bodies are byte-identical to the originals except that the module scene
// state (roomContainer, cobwebContainer, cobwebSpriteMap) is read/written through
// `scene` — the same live accessor object RoomCanvas passes to useRoomScene — and
// the map-singleton renderer is obtained via the `getRenderer` parameter.
// queryClient comes from useQueryClient() here (same provider instance).
//
// Tap path: optimistically destroy the sprite on tap (instant feedback), track
// pending ids so a mid-flight sync (stale query still listing them) does not
// re-add them, then delete + invalidateQueries. On failure, drop the pending id
// and re-invalidate so the server list re-creates the sprite.

export interface UseCobwebSyncParams {
  mapObjectId: number;
  isSceneReady: boolean;
  showGridMode: boolean;
  cobwebs: CobwebItem[];
  /** Ref to module-level PIXI roots — *Ref so immutability rule allows container writes. */
  pixiRootsRef: React.MutableRefObject<RoomPixiRoots>;
  getRenderer: () => PIXI.Renderer | undefined;
  sceneRef: React.MutableRefObject<RoomSceneState>;
}

export function useCobwebSync({
  mapObjectId,
  isSceneReady,
  showGridMode,
  cobwebs,
  pixiRootsRef,
  getRenderer,
  sceneRef,
}: UseCobwebSyncParams): void {
  const queryClient = useQueryClient();
  const { soundsEnabled } = useSoundStore();
  const [playCobwebSweep, { sound: cobwebSweepHowl }] = useSound('/game-assets/sounds/cobweb-sweep.mp3', { volume: 0.12, soundEnabled: soundsEnabled });
  const cobwebSweepHowlRef = useLatest(cobwebSweepHowl);
  const playCobwebWithFade = useCallback(() => {
    playCobwebSweep();
    const howl = cobwebSweepHowlRef.current;
    if (howl) {
      howl.fade(0, 0.125, 300);
      const durationMs = (howl.duration() || 2) * 1000;
      setTimeout(() => { cobwebSweepHowlRef.current?.fade(0.125, 0, 200); }, Math.max(0, durationMs - 220));
    }
  }, [playCobwebSweep, cobwebSweepHowlRef]);
  const playCobwebSweepRef = useLatest(playCobwebWithFade);
  // Ids removed on tap before the server list catches up — filtered out of sync.
  const pendingOptimisticDeletesRef = useRef<Set<number>>(new Set());

  // Lightweight effect: toggle cobweb footprint visibility when grid mode changes
  // Does NOT re-render cobweb sprites — avoids the sprite-height race condition.
  useEffect(() => {
    if (sceneRef.current.overlays.cobwebFp) {
      sceneRef.current.overlays.cobwebFp.visible = showGridMode;
    }
  }, [showGridMode, sceneRef]);

  // Drop pending flags once the query no longer lists those ids (server confirmed).
  useEffect(() => {
    const liveIds = new Set(cobwebs.map((w) => w.id));
    for (const id of pendingOptimisticDeletesRef.current) {
      if (!liveIds.has(id)) pendingOptimisticDeletesRef.current.delete(id);
    }
  }, [cobwebs]);

  // Render cobweb PIXI sprites — incremental: only add/remove changed sprites so that
  // surviving cobwebs are never destroyed/recreated (prevents visual jump artifacts).
  // NOTE: showGridMode intentionally NOT in deps — footprint visibility is handled above.
  useEffect(() => {
    const roots = pixiRootsRef.current;
    const roomContainer = roots.roomContainer;
    if (!isSceneReady || !roomContainer) return;

    // Exclude optimistically-deleted webs so a stale cobwebs snapshot cannot re-add them.
    const pending = pendingOptimisticDeletesRef.current;
    const visibleCobwebs = pending.size === 0
      ? cobwebs
      : cobwebs.filter((w) => !pending.has(w.id));

    // Sync logic extracted 1:1 to ./room/render/cobweb.ts (syncCobwebSprites).
    // It owns the incremental add/remove, footprint-debug overlay, and sprite
    // rasterization; the tap side effects (sweep/haptic/analytics/delete) stay here.
    const { cobwebContainer: nextContainer, fpGraphics } = syncCobwebSprites({
      roomContainer,
      cobwebContainer: roots.cobwebContainer,
      spriteMap: roots.cobwebSpriteMap,
      cobwebs: visibleCobwebs,
      showGridMode: sceneRef.current.flags.showGridMode,
      prevFpGraphics: sceneRef.current.overlays.cobwebFp,
      interiorMarkers: sceneRef.current.furniture.markers,
      renderer: getRenderer(),
      onCobwebTap: (cobwebId) => {
        playCobwebSweepRef.current();
        try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('medium'); } catch { /* optional */ }
        posthog.capture('cobweb_removed', { map_object_id: mapObjectId });

        // Instant visual feedback: destroy sprite now, keep id pending so sync
        // does not re-add it while deleteCobweb is in flight.
        pendingOptimisticDeletesRef.current.add(cobwebId);
        const spriteMap = roots.cobwebSpriteMap;
        const existing = spriteMap.get(cobwebId);
        if (existing) {
          destroyCobwebSprite(existing);
          spriteMap.delete(cobwebId);
        }

        deleteCobweb(mapObjectId, cobwebId)
          .then(() => queryClient.invalidateQueries({ queryKey: ['cobwebs', mapObjectId] }))
          .catch(() => {
            // Restore: allow sync to re-create from the still-present query item.
            pendingOptimisticDeletesRef.current.delete(cobwebId);
            queryClient.invalidateQueries({ queryKey: ['cobwebs', mapObjectId] });
          });
      },
    });
    roots.cobwebContainer = nextContainer;
    sceneRef.current.overlays.cobwebFp = fpGraphics;
    // showGridMode omitted — окремий effect вище (інакше full sprite rebuild).
    // getRenderer — нова () => mapSceneSingleton… стрілка щорендеру з RoomCanvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRenderer unstable; showGridMode separate
  }, [isSceneReady, cobwebs, mapObjectId, queryClient, pixiRootsRef, sceneRef]);
}
